import { ArrowBack } from '@mui/icons-material'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material'
import { type ReactNode, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

interface OnboardingWrapperProps {
  children: ReactNode
}

type OnboardingStep = 'content' | 'customize' | 'preview' | 'conversations'

const ONBOARDING_STORAGE_PREFIX = 'WEB_CONCIERGE_ONBOARDING_'

const ONBOARDING_CONTENT = {
  content: {
    title: 'Your AI Content is Ready!',
    message:
      "We've generated FAQ content from your main page! You can edit/add content here, or continue to customize your concierge's look and feel.",
    nextRoute: '/web-concierge/customize',
    nextStep: 'customize' as OnboardingStep,
    primaryButtonText: 'Edit now',
    secondaryButtonText: 'Skip to next',
  },
  customize: {
    title: 'Customize Your Concierge',
    message:
      'Here you can customize the look and feel of your concierge. You can always come back to make changes later.',
    nextRoute: '/web-concierge/preview',
    nextStep: 'preview' as OnboardingStep,
    prevRoute: '/web-concierge/content',
    prevStep: 'content' as OnboardingStep,
    primaryButtonText: 'Customize',
    secondaryButtonText: 'Skip to next',
  },
  preview: {
    title: 'Preview Your Concierge',
    message:
      'Give it a test run! Instructions for installing to your site are on the top right.',
    nextRoute: '/web-concierge/conversations',
    nextStep: 'conversations' as OnboardingStep,
    prevRoute: '/web-concierge/customize',
    prevStep: 'customize' as OnboardingStep,
    primaryButtonText: 'Try it out',
    secondaryButtonText: 'Skip to next',
  },
  conversations: {
    title: 'View Conversations',
    message:
      "This is where you'll see all the conversations your concierge has with your website visitors. Great job setting everything up!",
    prevRoute: '/web-concierge/preview',
    prevStep: 'preview' as OnboardingStep,
    primaryButtonText: 'OK',
    showSecondaryButton: false,
  },
}

function extractStepFromPath(pathname: string): OnboardingStep | null {
  if (pathname.includes('/content')) return 'content'
  if (pathname.includes('/customize')) return 'customize'
  if (pathname.includes('/preview')) return 'preview'
  if (pathname.includes('/conversations')) return 'conversations'
  return null
}

function isStepViewed(step: OnboardingStep): boolean {
  try {
    return (
      localStorage.getItem(`${ONBOARDING_STORAGE_PREFIX}${step}`) === 'true'
    )
  } catch {
    return false
  }
}

function markStepAsViewed(step: OnboardingStep): void {
  try {
    localStorage.setItem(`${ONBOARDING_STORAGE_PREFIX}${step}`, 'true')
  } catch {
    // Silently fail in private browsing or when storage quota exceeded
  }
}

function clearStepViewed(step: OnboardingStep): void {
  try {
    localStorage.removeItem(`${ONBOARDING_STORAGE_PREFIX}${step}`)
  } catch {
    // Silently fail in private browsing
  }
}

export default function OnboardingWrapper({
  children,
}: OnboardingWrapperProps) {
  const { pathname } = useLocation()
  const navigate = useNavigate()

  const currentStep = extractStepFromPath(pathname)
  const [isCurrentStepViewed, setIsCurrentStepViewed] = useState(() =>
    currentStep ? isStepViewed(currentStep) : false,
  )

  const shouldShowOnboarding = currentStep && !isCurrentStepViewed
  const stepContent = currentStep ? ONBOARDING_CONTENT[currentStep] : null

  // Update state when step changes
  useEffect(() => {
    if (currentStep) {
      setIsCurrentStepViewed(isStepViewed(currentStep))
    }
  }, [currentStep])

  function handleContinueOnboarding(): void {
    if (!currentStep) return

    markStepAsViewed(currentStep)
    setIsCurrentStepViewed(true)
    if (stepContent?.nextRoute) {
      navigate(stepContent.nextRoute)
    }
  }

  function handleEditContent(): void {
    if (!currentStep) return
    markStepAsViewed(currentStep)
    setIsCurrentStepViewed(true)
  }

  function handleGoBack(): void {
    if (!currentStep || !stepContent?.prevStep || !stepContent?.prevRoute)
      return

    clearStepViewed(currentStep)
    clearStepViewed(stepContent.prevStep)
    navigate(stepContent.prevRoute)
  }

  return (
    <>
      {children}
      <Dialog
        open={Boolean(shouldShowOnboarding)}
        onClose={handleEditContent}
        aria-labelledby='onboarding-title'
        aria-describedby='onboarding-description'
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle
          id='onboarding-title'
          sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}
        >
          {stepContent?.prevRoute && (
            <IconButton
              onClick={handleGoBack}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
              }}
              aria-label='Go back'
            >
              <ArrowBack />
            </IconButton>
          )}
          <Box
            sx={{
              flex: 1,
              textAlign: 'center',
              px: stepContent?.prevRoute ? 6 : 0,
            }}
          >
            {stepContent?.title}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography id='onboarding-description'>
            {stepContent?.message}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            padding: 2,
            justifyContent:
              stepContent?.showSecondaryButton === false
                ? 'center'
                : 'space-between',
          }}
        >
          <Button
            onClick={handleEditContent}
            variant='contained'
            color='primary'
          >
            {stepContent?.primaryButtonText}
          </Button>
          {stepContent?.showSecondaryButton !== false && (
            <Button onClick={handleContinueOnboarding} variant='outlined'>
              {stepContent?.secondaryButtonText}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}
